import React, { useCallback, useEffect, useRef } from 'react'
import { CheckCircleOutlined, ScanOutlined, WarningOutlined } from '@ant-design/icons'
import {
  FACE_SESSION_STATUS,
  getFaceSessionStream,
  startFaceSession
} from '@/views/surveillance/face/faceCaptureSession'

/**
 * 去向盘内本机摄像头取景 + 脸部对准框（非萤石监控流）
 * 识别失败或已确认后，均可点击取景框重新拍（须清掉旧结果后再识别）
 */
export default function FaceAlignPreview ({
  cellKey,
  faceSnapshot,
  active = false,
  /** 重新拍：由父级先清空旧结果再 startFaceSession，避免 READY 残留可点 */
  onRecapture
}) {
  const videoRef = useRef(null)
  const status = faceSnapshot?.status
  const streamActive = Boolean(faceSnapshot?.streamActive)
  const nurseName = faceSnapshot?.result?.nurseName
  const isCamUnavailable = faceSnapshot?.reason === 'permission'
    || faceSnapshot?.reason === 'device'
  const isLive = active
    && streamActive
    && (
      status === FACE_SESSION_STATUS.REQUESTING_PERMISSION
      || status === FACE_SESSION_STATUS.RECOGNIZING
    )
  const isReady = active && status === FACE_SESSION_STATUS.READY
  const isFailed = active && status === FACE_SESSION_STATUS.FAILED
  const canRetry = (isFailed || isReady) && Boolean(cellKey)
  const isWaitingCam = active
    && !streamActive
    && (
      status === FACE_SESSION_STATUS.REQUESTING_PERMISSION
      || status === FACE_SESSION_STATUS.RECOGNIZING
      || status === FACE_SESSION_STATUS.IDLE
      || !status
    )

  useEffect(() => {
    const video = videoRef.current
    if (!video) return undefined

    if (!isLive || !cellKey) {
      video.srcObject = null
      return undefined
    }

    const stream = getFaceSessionStream(cellKey)
    if (!stream) {
      video.srcObject = null
      return undefined
    }

    video.srcObject = stream
    const playPromise = video.play()
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {})
    }

    return () => {
      video.srcObject = null
    }
  }, [cellKey, isLive, streamActive, status])

  const handleRetry = useCallback((e) => {
    if (!canRetry) return
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation()
    if (typeof onRecapture === 'function') {
      onRecapture()
      return
    }
    startFaceSession(cellKey)
  }, [canRetry, cellKey, onRecapture])

  const handleRetryKeyDown = useCallback((e) => {
    if (!canRetry) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleRetry(e)
    }
  }, [canRetry, handleRetry])

  return (
    <div className="surveillance-face-align" aria-live="polite">
      <div
        className={[
          'surveillance-face-align__stage',
          isLive ? 'surveillance-face-align__stage--live' : '',
          isReady ? 'surveillance-face-align__stage--ready' : '',
          isFailed ? 'surveillance-face-align__stage--failed' : '',
          canRetry ? 'surveillance-face-align__stage--retryable' : ''
        ].filter(Boolean).join(' ')}
        role={canRetry ? 'button' : undefined}
        tabIndex={canRetry ? 0 : undefined}
        aria-label={canRetry ? '重新拍' : undefined}
        onClick={canRetry ? handleRetry : undefined}
        onKeyDown={canRetry ? handleRetryKeyDown : undefined}
      >
        <video
          ref={videoRef}
          className="surveillance-face-align__video"
          muted
          playsInline
          autoPlay
        />

        <div className="surveillance-face-align__frame" aria-hidden="true">
          <span className="surveillance-face-align__corner surveillance-face-align__corner--tl" />
          <span className="surveillance-face-align__corner surveillance-face-align__corner--tr" />
          <span className="surveillance-face-align__corner surveillance-face-align__corner--bl" />
          <span className="surveillance-face-align__corner surveillance-face-align__corner--br" />
        </div>

        {isReady ? (
          <div className="surveillance-face-align__overlay">
            <CheckCircleOutlined className="surveillance-face-align__overlay-icon" />
            <span className="surveillance-face-align__overlay-text">
              {nurseName ? `已确认：${nurseName}` : '已确认身份'}
            </span>
            <span className="surveillance-face-align__overlay-action">点击重新拍</span>
          </div>
        ) : null}

        {isFailed ? (
          <div className="surveillance-face-align__overlay">
            <WarningOutlined className="surveillance-face-align__overlay-icon" />
            <span className="surveillance-face-align__overlay-text">
              {isCamUnavailable ? '摄像头不可用' : '未拍清或未识别到人'}
            </span>
            <span className="surveillance-face-align__overlay-action">点击重新拍</span>
          </div>
        ) : null}

        {isWaitingCam ? (
          <div className="surveillance-face-align__overlay surveillance-face-align__overlay--dim">
            <ScanOutlined className="surveillance-face-align__overlay-icon" />
            <span className="surveillance-face-align__overlay-text">正在打开摄像头…</span>
          </div>
        ) : null}
      </div>
      <p className="surveillance-face-align__hint">
        {isReady
          ? '选好去向；拍糊了可点取景框重拍'
          : isFailed
            ? (
              isCamUnavailable
                ? '检查权限后，再点取景框重拍'
                : '点取景框再拍一次'
            )
            : '请正对屏幕，脸对准框内'}
      </p>
    </div>
  )
}
